export const RTC_SFU_RECONNECTION_STATE = Object.freeze({
  CONNECTED: "connected",
  FAILED: "failed",
  PROVISIONING: "provisioning",
  REALLOCATING: "reallocating",
  RECONNECTING: "reconnecting"
});

export const DEFAULT_RTC_SFU_RECONNECTION_OPTIONS = Object.freeze({
  sameAllocationAttempts: 3,
  freshAllocationAttempts: 2,
  connectAttemptTimeoutMs: 5000,
  baseDelayMs: 500,
  maxDelayMs: 3000,
  jitterRatio: 0.25
});

function mergedOptions(options = {}) {
  return {
    ...DEFAULT_RTC_SFU_RECONNECTION_OPTIONS,
    ...options
  };
}

export function calculateRtcSfuReconnectDelay({
  attempt,
  baseDelayMs = DEFAULT_RTC_SFU_RECONNECTION_OPTIONS.baseDelayMs,
  maxDelayMs = DEFAULT_RTC_SFU_RECONNECTION_OPTIONS.maxDelayMs,
  jitterRatio = DEFAULT_RTC_SFU_RECONNECTION_OPTIONS.jitterRatio,
  random = Math.random
}) {
  const exponentialDelay = baseDelayMs * Math.pow(2, Math.max(0, attempt - 1));
  const boundedDelay = Math.min(exponentialDelay, maxDelayMs);
  const jitter = boundedDelay * jitterRatio * (random() * 2 - 1);

  return Math.max(0, Math.round(boundedDelay + jitter));
}

async function connectWithTimeout({ endpoint, connect, timeoutMs }) {
  if (!timeoutMs || timeoutMs <= 0) {
    await connect(endpoint);

    return;
  }

  let timeoutId;
  try {
    await Promise.race([
      connect(endpoint),
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error("RTC SFU signaling connection timed out."));
        }, timeoutMs);
      })
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}

async function retryEndpoint({ endpoint, attempts, connect, sleep, random, options }) {
  let lastError;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (attempt > 1) {
      await sleep(calculateRtcSfuReconnectDelay({ attempt: attempt - 1, random, ...options }));
    }

    try {
      await connectWithTimeout({
        endpoint,
        connect,
        timeoutMs: options.connectAttemptTimeoutMs
      });

      return { endpoint };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

export async function recoverRtcSfuConnection({
  endpoint,
  connect,
  resolveFreshEndpoint,
  onStateChange = () => {},
  sleep = delay => new Promise(resolve => setTimeout(resolve, delay)),
  random = Math.random,
  options = {}
}) {
  const retryOptions = mergedOptions(options);
  let lastError;

  onStateChange(RTC_SFU_RECONNECTION_STATE.RECONNECTING);

  try {
    const result = await retryEndpoint({
      endpoint,
      attempts: retryOptions.sameAllocationAttempts,
      connect,
      sleep,
      random,
      options: retryOptions
    });

    onStateChange(RTC_SFU_RECONNECTION_STATE.CONNECTED);

    return result;
  } catch (error) {
    lastError = error;
  }

  if (!resolveFreshEndpoint) {
    onStateChange(RTC_SFU_RECONNECTION_STATE.FAILED);
    throw lastError;
  }

  onStateChange(RTC_SFU_RECONNECTION_STATE.REALLOCATING);

  try {
    const freshEndpoint = await resolveFreshEndpoint({ onStateChange });
    const result = await retryEndpoint({
      endpoint: freshEndpoint,
      attempts: retryOptions.freshAllocationAttempts,
      connect,
      sleep,
      random,
      options: retryOptions
    });

    onStateChange(RTC_SFU_RECONNECTION_STATE.CONNECTED);

    return result;
  } catch (error) {
    onStateChange(RTC_SFU_RECONNECTION_STATE.FAILED);
    throw error;
  }
}
